import { ref } from 'vue'
import produtoService from '@/services/produtoService'

export function useProduto() {
  const produto = ref({
    id: null,
    name: '',
    description: '',
    mobilePicture: undefined,
    mobilePictureUrl: '',
    desktopPicture: undefined,
    desktopPictureUrl: '',
    technicalSheet: undefined,
    technicalSheetUrl: '',
    certification: undefined,
    certificationUrl: '',
    categories: [], // Array de IDs das categorias selecionadas
    associatedCategories: [], // Dados completos das categorias da API
    manuals: [],
    associatedManuals: [],
    disabled: false,
    // Campos para o frontend (compatibilidade)
    categoriaId: null, // Para o select principal
    categoriasVinculadas: [], // Array formatado para exibição
    // Arquivos para upload
    imagemMobileFile: null,
    imagemDesktopFile: null,
    imagemMobilePreview: '',
    imagemDesktopPreview: '',
    technicalSheetFile: null,
    certificationFile: null
  })

  const saving = ref(false)
  const error = ref(null)

  /**
   * Carrega dados de um produto específico
   * @param {string|number} id - ID do produto
   */
  const carregarProduto = async (id) => {
    try {
      error.value = null
      
      const response = await produtoService.buscarPorId(id)
      
      if (response?.data) {
        // Mapear dados da API para o formato do frontend
        const produtoAPI = response.data
        
        produto.value = {
          id: produtoAPI.id,
          name: produtoAPI.name || '',
          description: produtoAPI.description || '',
          mobilePicture: undefined,
          mobilePictureUrl: produtoAPI.mobilePictureUrl || '',
          desktopPicture: undefined,
          desktopPictureUrl: produtoAPI.desktopPictureUrl || '',
          technicalSheet: undefined,
          technicalSheetUrl: produtoAPI.technicalSheetUrl || '',
          certification: undefined,
          certificationUrl: produtoAPI.certificationUrl || '',
          categories: produtoAPI.categories || [],
          associatedCategories: produtoAPI.associatedCategories || [],
          manuals: produtoAPI.manuals || [],
          associatedManuals: produtoAPI.associatedManuals || [],
          disabled: Boolean(produtoAPI.disabled), // Garantir que seja boolean
          // Campos para compatibilidade com o frontend
          categoriaId: produtoAPI.categories?.[0] || null,
          categoriasVinculadas: [], // Será preenchido depois com atualizarCategoriasVinculadas
          // Arquivos para upload
          imagemMobileFile: null,
          imagemDesktopFile: null,
          imagemMobilePreview: '',
          imagemDesktopPreview: '',
          technicalSheetFile: null,
          certificationFile: null
        }
        
        return response.data;
      } else {
        throw new Error('Dados do produto não encontrados')
      }
    } catch (err) {
      console.error('❌ Erro ao carregar produto:', err)
      error.value = err.message || 'Erro ao carregar produto'
      return false
    }
  }

  /**
   * Salva o produto (criar ou atualizar)
   */
  const salvarProduto = async () => {
    try {
      saving.value = true
      error.value = null
      
      // Preparar dados para envio
      const dadosParaEnvio = {
        name: produto.value.name,
        description: produto.value.description,
        categories: produto.value.categories,
        manuals: produto.value.manuals,
        disabled: Boolean(produto.value.disabled) // Garantir que seja boolean
      }
      
      // Adicionar ID apenas se for atualização e existir
      if (produto.value.id) {
        dadosParaEnvio.id = Number(produto.value.id)
      }
      
      // Preparar arquivos se existirem
      const files = {}
      if (produto.value.imagemMobileFile) {
        files.mobilePicture = produto.value.imagemMobileFile
      }
      if (produto.value.imagemDesktopFile) {
        files.desktopPicture = produto.value.imagemDesktopFile
      }
      if (produto.value.technicalSheetFile) {
        files.technicalSheet = produto.value.technicalSheetFile
      }
      if (produto.value.certificationFile) {
        files.certification = produto.value.certificationFile
      }
      
      let response
      
      if (produto.value.id) {
        // Atualizar produto existente
        response = await produtoService.atualizar(dadosParaEnvio, Object.keys(files).length > 0 ? files : null)
      } else {
        // Criar novo produto
        response = await produtoService.criar(dadosParaEnvio, Object.keys(files).length > 0 ? files : null)
      }
      
      if (response?.data) {
        // Atualizar dados locais com resposta da API se necessário
        if (response.data.id) {
          produto.value.id = response.data.id
        }
        
        return true
      } else {
        throw new Error('Resposta inválida da API')
      }
    } catch (err) {
      console.error('❌ Erro ao salvar produto:', err)
      error.value = err.message || 'Erro ao salvar produto'
      return false
    } finally {
      saving.value = false
    }
  }

  /**
   * Processa upload de arquivo (imagem ou PDF)
   * @param {File} arquivo - Arquivo de imagem ou PDF
   * @param {string} tipo - Tipo do arquivo ('mobile', 'desktop', 'technicalSheet', 'certification')
   */
  const processarImagem = (arquivo, tipo = 'mobile') => {
    if (!arquivo) return

    // Validações baseadas no tipo
    if (tipo === 'technicalSheet' || tipo === 'certification') {
      // Validação para PDFs
      if (arquivo.type !== 'application/pdf') {
        error.value = 'Por favor, selecione apenas arquivos PDF'
        return
      }
      
      if (arquivo.size > 10 * 1024 * 1024) {
        error.value = 'O arquivo PDF deve ter no máximo 10MB'
        return
      }
      
      // Armazenar arquivo PDF
      if (tipo === 'technicalSheet') {
        produto.value.technicalSheetFile = arquivo
      } else {
        produto.value.certificationFile = arquivo
      }
    } else {
      // Validação para imagens
      if (!arquivo.type.startsWith('image/')) {
        error.value = 'Por favor, selecione apenas arquivos de imagem'
        return
      }

      if (arquivo.size > 5 * 1024 * 1024) {
        error.value = 'A imagem deve ter no máximo 5MB'
        return
      }

      // Armazenar arquivo baseado no tipo
      if (tipo === 'mobile') {
        produto.value.imagemMobileFile = arquivo
        
        // Criar preview
        const reader = new FileReader()
        reader.onload = (e) => {
          produto.value.imagemMobilePreview = e.target?.result
        }
        reader.readAsDataURL(arquivo)
      } else {
        produto.value.imagemDesktopFile = arquivo
        
        // Criar preview
        const reader = new FileReader()
        reader.onload = (e) => {
          produto.value.imagemDesktopPreview = e.target?.result
        }
        reader.readAsDataURL(arquivo)
      }
    }

    // Limpar erro se existir
    if (error.value) {
      error.value = null
    }
  }

  /**
   * Reset do produto para estado inicial
   */
  const resetProduto = () => {
    produto.value = {
      id: null,
      name: '',
      description: '',
      mobilePicture: undefined,
      mobilePictureUrl: '',
      desktopPicture: undefined,
      desktopPictureUrl: '',
      technicalSheet: undefined,
      technicalSheetUrl: '',
      certification: undefined,
      certificationUrl: '',
      categories: [],
      associatedCategories: [],
      manuals: [],
      associatedManuals: [],
      disabled: false,
      // Campos para compatibilidade com o frontend
      categoriaId: null,
      categoriasVinculadas: [],
      // Arquivos para upload
      imagemMobileFile: null,
      imagemDesktopFile: null,
      imagemMobilePreview: '',
      imagemDesktopPreview: '',
      technicalSheetFile: null,
      certificationFile: null
    }
  }

  /**
   * Adiciona uma categoria ao produto
   * @param {number} categoriaId - ID da categoria
   * @param {object} categoriaData - Dados completos da categoria (nome, cores, etc)
   */
  const adicionarCategoria = (categoriaId, categoriaData) => {
    if (!categoriaId || produto.value.categories.includes(categoriaId)) {
      return // Não adicionar se já existe ou ID inválido
    }

    produto.value.categories.push(categoriaId)
    produto.value.categoriasVinculadas.push({
      id: categoriaId,
      name: categoriaData.nome || categoriaData.name,
      tagBackgroundColor: categoriaData.tagBackgroundColor,
      tagForegroundColor: categoriaData.tagForegroundColor
    })

    // Atualizar categoriaId principal se for a primeira categoria
    if (produto.value.categories.length === 1) {
      produto.value.categoriaId = categoriaId
    }
  }

  /**
   * Remove uma categoria do produto
   * @param {number} categoriaId - ID da categoria a remover
   */
  const removerCategoria = (categoriaId) => {
    const index = produto.value.categories.indexOf(categoriaId)
    if (index > -1) {
      produto.value.categories.splice(index, 1)
      produto.value.categoriasVinculadas = produto.value.categoriasVinculadas.filter(
        cat => cat.id !== categoriaId
      )

      // Atualizar categoriaId principal
      if (produto.value.categoriaId === categoriaId) {
        produto.value.categoriaId = produto.value.categories.length > 0 
          ? produto.value.categories[0] 
          : null
      }
    }
  }

  /**
   * Atualiza as categorias vinculadas com base nos dados das categorias disponíveis
   * @param {object} categoriasPorId - Mapeamento de categorias por ID
   */
  const atualizarCategoriasVinculadas = (categoriasPorId) => {
    produto.value.categoriasVinculadas = produto.value.categories.map(catId => {
      const categoriaData = categoriasPorId[catId]
      return {
        id: catId,
        name: categoriaData?.name || `Categoria ${catId}`,
        tagBackgroundColor: categoriaData?.tagBackgroundColor || '#e0e0e0',
        tagForegroundColor: categoriaData?.tagForegroundColor || '#000000'
      }
    })
  }

  return {
    produto,
    saving,
    error,
    carregarProduto,
    salvarProduto,
    processarImagem,
    resetProduto,
    adicionarCategoria,
    removerCategoria,
    atualizarCategoriasVinculadas
  }
}

export default useProduto
